import { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { LogOut, Menu, Settings, X } from 'lucide-react';
import { Logo } from '../brand/Logo';
import { ThemeToggle } from './ThemeToggle';
import { cn } from '@/lib/cn';
import { useAuth } from '@/store/auth';
import { toast } from '@/store/toast';
import { useT } from '@/i18n';
import type { TranslationKey } from '@/i18n';
import { LanguagePicker } from './LanguagePicker';

interface NavItem {
  to: string;
  end?: boolean;
}

const SENDER_NAV: Array<NavItem & { labelKey: TranslationKey }> = [
  { to: '/app', labelKey: 'nav.myVideos', end: true },
  { to: '/app/settings', labelKey: 'nav.privacy' },
];

/** Centred nav with the lime underline on the active item, as in the reference. */
function NavLinks({
  items,
  onNavigate,
}: {
  items: Array<NavItem & { labelKey: TranslationKey }>;
  onNavigate?: () => void;
}) {
  const t = useT();
  return (
    <>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'relative py-2 text-[0.9375rem] transition-colors duration-fast',
              isActive ? 'text-ink' : 'text-muted hover:text-ink',
              // The underline is a pseudo-element so it never shifts layout.
              isActive &&
                "after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:bg-accent-graphic after:content-['']",
            )
          }
        >
          {t(item.labelKey)}
        </NavLink>
      ))}
    </>
  );
}

function initialsOf(email: string | null | undefined): string {
  if (!email) return '?';
  return email.slice(0, 2).toUpperCase();
}

export function AppHeader() {
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const navigate = useNavigate();
  const t = useT();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch {
      toast.error(t('settings.signOutFailed'));
    }
  };

  return (
    <header className="sticky top-0 z-40 border-b border-hairline bg-bg/85 backdrop-blur-md">
      <div className="mx-auto flex h-header max-w-content items-center gap-4 px-4 sm:px-6">
        <Logo to="/app" />

        <nav aria-label={t('nav.main')} className="ml-8 hidden flex-1 gap-8 md:flex">
          <NavLinks items={SENDER_NAV} />
        </nav>

        <div className="ml-auto flex items-center gap-1 md:ml-0">
          <LanguagePicker className="hidden sm:inline-flex" />
          <ThemeToggle />

          <Link
            to="/app/settings"
            aria-label={t('nav.settings')}
            className="hidden size-9 place-items-center rounded-sm text-muted transition-colors hover:bg-surface-raised hover:text-ink md:grid"
          >
            <Settings className="size-[1.125rem]" strokeWidth={1.75} aria-hidden="true" />
          </Link>

          <button
            type="button"
            onClick={handleLogout}
            aria-label={t('nav.signOut')}
            className="hidden size-9 place-items-center rounded-sm text-muted transition-colors hover:bg-surface-raised hover:text-ink md:grid"
          >
            <LogOut className="size-[1.125rem]" strokeWidth={1.75} aria-hidden="true" />
          </button>

          {/* Avatar, matching the reference's top-right identity chip. */}
          <span
            aria-hidden="true"
            title={user?.email ?? undefined}
            className="ml-2 hidden size-9 place-items-center rounded-full border border-hairline-strong bg-surface-raised text-xs font-medium text-muted md:grid"
          >
            {initialsOf(user?.email)}
          </span>

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            aria-label={menuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
            className="grid size-9 place-items-center rounded-sm text-muted transition-colors hover:text-ink md:hidden"
          >
            {menuOpen ? (
              <X className="size-5" aria-hidden="true" />
            ) : (
              <Menu className="size-5" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div id="mobile-nav" className="border-t border-hairline md:hidden">
          <nav
            aria-label={t('nav.main')}
            className="mx-auto flex max-w-content flex-col gap-1 px-4 py-3"
          >
            <NavLinks items={SENDER_NAV} onNavigate={() => setMenuOpen(false)} />
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-2 py-2 text-left text-[0.9375rem] text-muted transition-colors hover:text-ink"
            >
              <LogOut className="size-4" aria-hidden="true" />
              {t('nav.signOut')}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
